"use client";
import React, { useMemo, FC } from "react";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import NextLink from "next/link";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import { BREADCRUMBS_PANEL_CLASSES } from "./constants";
import { BreadcrumbsPanelContainer } from "./styled";

export interface BreadcrumbsPanelProps {
  breadcrumbs: Nullable<BreadcrumbsItem>[];
  showAll?: boolean;
  isArticlePage?: boolean;
}

export const BreadcrumbsPanel: FC<BreadcrumbsPanelProps> = ({
  breadcrumbs,
  showAll,
  isArticlePage = false,
}) => {
  const { locale } = useParams<GlobalPathParams>();
  const hasBreadcrumbs = isArticlePage
    ? !!breadcrumbs
    : !!breadcrumbs && breadcrumbs?.length > 1;

  const breadCrumbsList = useMemo(() => {
    if (breadcrumbs) {
      const breadcrumbsReversed = breadcrumbs.slice().reverse();
      return showAll ? breadcrumbsReversed : breadcrumbsReversed.slice(0, -1);
    }
  }, [breadcrumbs, showAll]);

  return (
    <BreadcrumbsPanelContainer
      data-evergage="breadcrumbs"
      id="breadcrumbs-panel"
      {...getTestAutomationProps("breadcrumbs-pannel")}
      sx={{
        display: hasBreadcrumbs ? "block" : "none",
        paddingLeft: {
          xs: 0,
          sm: 0,
          md: 0,
          lg: 0,
          xl: 0,
        },
      }}
    >
      <Breadcrumbs className={BREADCRUMBS_PANEL_CLASSES.link}>
        {breadCrumbsList?.map((item) => (
          <NextLink
            prefetch={false}
            href={`/${locale}/${item?.urlSlug}`}
            locale={false}
            key={item?.id}
            passHref
            id={`breadcrumbs-list-item-${item?.id}`}
            data-evergage-breadcrumbs-link={item?.title}
            {...getTestAutomationProps("breadcrumbs-link")}
          >
            {item?.title ?? ""}
          </NextLink>
        ))}
      </Breadcrumbs>
    </BreadcrumbsPanelContainer>
  );
};
